import { getCurrentPlatform } from '@/helpers/Platform/Platform';
import { DefaultPrivacyLevel } from '@datadog/browser-core';
import { RumErrorEvent, RumEvent, RumEventDomainContext } from '@datadog/browser-rum';
import { captureError, initBrowserRum } from '@smg-real-estate/browser-rum';
import { isAxiosError } from 'axios';
import isNil from 'lodash.isnil';

export enum CustomAction {
  SubmitFunnel = 'Submit Funnel',
  VerifyOTP = 'Verify OTP',
}

export enum DDActionName {
  FunnelSubmitButton = 'Funnel Submit Button',
  VerifyOTPButton = 'Verify OTP Button',
  OfferTypeRadio = 'Offer Type Radio',
}

interface ErrorLogContext {
  endpoint: string;
  requestData?: unknown;
  feature?: string; // Feature name if applicable
  [key: string]: unknown; // Other contextual information
}

// Uncaught "Object Not Found Matching Id:2, MethodName:update, ParamCount:4"
// https://github.com/DataDog/browser-sdk/issues/2715
const isUncaughtObjectNotFoundError = (error: RumErrorEvent['error']) => {
  const regex = /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;
  return regex.test(error.message);
};

// Uncaught {"type":"error","isTrusted":true,"currentTarget":null,"target":"[HTMLScriptElement]"}
const isUncaughtIsScriptLoadError = (error: RumErrorEvent['error']) => {
  const regex = /\{"type":"error","isTrusted":true,"currentTarget":null,"target":"\[.*?\]"\}/;
  return regex.test(error.message);
};

const isInlineMetaBrowserCallbackError = (error: RumErrorEvent['error']) => {
  const regex = /Can't find variable: _AutofillCallbackHandler/;
  return regex.test(error.message);
};

// PP-3925: Session replays confirm the chart continues rendering after this
// Benign Chromium warning often triggered by ApexCharts resize handling during layout.
const isResizeObserverLoopError = (error: RumErrorEvent['error']) => {
  const regex = /ResizeObserver loop completed with undelivered notifications/;
  return regex.test(error.message);
};

export const initDatadogRUM = (): void => {
  initBrowserRum({
    applicationId: import.meta.env.VUE_DATADOG_RUM_APPLICATION_ID,
    clientToken: import.meta.env.VUE_DATADOG_RUM_CLIENT_TOKEN,
    service: `${getCurrentPlatform()}-property-valuation-vue`,
    env: import.meta.env.VUE_APP_STAGE,
    version: import.meta.env.VUE_COMMIT_SHA,
    sessionSampleRate: 20,
    sessionReplaySampleRate: 10,
    defaultPrivacyLevel: DefaultPrivacyLevel.MASK,
    beforeSend: (event: RumEvent, _context: RumEventDomainContext) => {
      if (event.type !== 'error') {
        return true;
      }
      const error: RumErrorEvent['error'] = event.error;
      // Filter out strange Uncaught errors
      return !(
        isUncaughtObjectNotFoundError(error) ||
        isUncaughtIsScriptLoadError(error) ||
        isInlineMetaBrowserCallbackError(error) ||
        isResizeObserverLoopError(error)
      );
    },

    trackingConsentEnabled: true,
    enabled: ['test', 'prod'].includes(import.meta.env.VUE_APP_STAGE),
    customAttributes: {
      platform: import.meta.env.VUE_APP_PLATFORM,
    },
  });
};

// Customize the axios error logging to include more information to help trace errors
export const logAxiosError = (error: unknown, context: ErrorLogContext): void => {
  if (!isAxiosError(error)) {
    captureError(error as Error, {
      type: 'non-axios-error',
      ...context,
    });
    return;
  }

  const safePayload = obfuscateSensitiveData(context.requestData || error.config?.data);

  const fullContext = {
    ...context,
    requestData: safePayload,
    axios: {
      config: {
        url: error.config?.url,
        method: error.config?.method,
        params: error.config?.params,
      },
      ...(error.response && {
        response: {
          status: error.response.status,
          data: error.response.data,
        },
      }),
    },
  };

  const errorMessage = [
    `API Error: ${context.endpoint}`,
    error.response?.status && `Status: ${error.response.status}`,
    error.message,
  ]
    .filter(Boolean)
    .join(' | ');

  const loggedError = new Error(errorMessage);
  if (error.stack) loggedError.stack = error.stack;

  captureError(loggedError, fullContext);
};

const obfuscateSensitiveData = (data: unknown): unknown => {
  if (!data || typeof data !== 'object') return data;

  const sensitiveKeys = ['email', 'name', 'phoneNumber'];
  return Object.keys(data).reduce(
    (result, key) => {
      const value = data[key];
      if (!isNil(value) && sensitiveKeys.includes(key)) {
        result[key] = '*****';
      } else if (typeof value === 'object') {
        result[key] = obfuscateSensitiveData(value); // loop through nested objects
      } else {
        result[key] = value;
      }
      return result;
    },
    {} as Record<string, unknown>,
  );
};
